import { z } from 'zod';

/**
 * The Finance service's public contract.
 *
 * A journal entry balances by construction — the sum of its lines' debits
 * always equals the sum of their credits — checked in the aggregate before
 * anything is persisted, not merely at this boundary.
 */

export const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'] as const;
export type AccountType = (typeof ACCOUNT_TYPES)[number];

export const createAccountRequestSchema = z.object({
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(200),
  type: z.enum(ACCOUNT_TYPES),
});
export type CreateAccountRequest = z.infer<typeof createAccountRequestSchema>;

export const journalEntryLineInputSchema = z.object({
  accountId: z.uuid(),
  debit: z.number().int().min(0).default(0),
  credit: z.number().int().min(0).default(0),
});
export type JournalEntryLineInput = z.infer<typeof journalEntryLineInputSchema>;

export const postJournalEntryRequestSchema = z.object({
  description: z.string().trim().min(1).max(500),
  entryDate: z.iso.datetime().nullish(),
  lines: z.array(journalEntryLineInputSchema).min(2),
});
export type PostJournalEntryRequest = z.infer<typeof postJournalEntryRequestSchema>;

export const listJournalEntriesQuerySchema = z.object({
  cursor: z.string().nullish(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  accountId: z.uuid().nullish(),
});
export type ListJournalEntriesQuery = z.infer<typeof listJournalEntriesQuerySchema>;

/** A period report needs both ends explicit — "this quarter" is a choice the
 *  caller makes, never a default this contract guesses at. */
export const periodReportQuerySchema = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
  })
  .refine((value) => value.from <= value.to, {
    message: '"from" must not be after "to"',
    path: ['from'],
  });
export type PeriodReportQuery = z.infer<typeof periodReportQuerySchema>;

export const balanceSheetQuerySchema = z.object({
  asOf: z.iso.date(),
});
export type BalanceSheetQuery = z.infer<typeof balanceSheetQuerySchema>;

// --- Responses --------------------------------------------------------------

export interface AccountResponse {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly type: AccountType;
  readonly isSystem: boolean;
  readonly archivedAt: string | null;
}

export interface JournalEntryLineResponse {
  readonly id: string;
  readonly accountId: string;
  readonly accountName: string;
  readonly debit: number;
  readonly credit: number;
}

export interface JournalEntryResponse {
  readonly id: string;
  readonly entryDate: string;
  readonly description: string;
  readonly sourceType: string | null;
  readonly sourceId: string | null;
  readonly createdByUserId: string | null;
  readonly lines: readonly JournalEntryLineResponse[];
}

export interface LedgerLineResponse {
  readonly journalEntryId: string;
  readonly entryDate: string;
  readonly description: string;
  readonly debit: number;
  readonly credit: number;
  /** Running balance after this line, in the account's natural sign. */
  readonly balance: number;
}

// --- Reports ------------------------------------------------------------------

export interface AccountAmountResponse {
  readonly accountId: string;
  readonly code: string;
  readonly name: string;
  /** Already in the account's natural sign — a REVENUE line here is
   *  positive for a normal credit balance, matching what `getAccountLedger`
   *  already does per account, not a raw debit-minus-credit. */
  readonly amount: number;
}

/**
 * Revenue and expense activity between two dates — a period report, not a
 * point-in-time one. An account with no activity in the range is simply
 * absent, not listed at zero.
 */
export interface IncomeStatementResponse {
  readonly from: string;
  readonly to: string;
  readonly revenue: readonly AccountAmountResponse[];
  readonly expense: readonly AccountAmountResponse[];
  readonly totalRevenue: number;
  readonly totalExpense: number;
  readonly netIncome: number;
}

/**
 * Every REVENUE account's activity in the period, under its own name —
 * "GENERAL", "MISSIONS" and so on are account names here, not a separate
 * `Fund` entity finance-service has no reason to know about. What this
 * reports is money *received* per fund, not a spendable balance: an
 * expense is never tagged back to the fund that paid for it, so "how much
 * is left in Missions" is a different, unbuilt question from "how much
 * came in for Missions".
 */
export interface FundBalancesResponse {
  readonly from: string;
  readonly to: string;
  readonly funds: readonly AccountAmountResponse[];
  readonly total: number;
}

/**
 * Every ASSET/LIABILITY/EQUITY account's cumulative balance as of one date
 * — unlike the income statement, there is no "from": a balance sheet is a
 * snapshot of everything posted up to that moment, not one period's
 * activity.
 */
export interface BalanceSheetResponse {
  readonly asOf: string;
  readonly assets: readonly AccountAmountResponse[];
  readonly liabilities: readonly AccountAmountResponse[];
  readonly equity: readonly AccountAmountResponse[];
  readonly totalAssets: number;
  readonly totalLiabilities: number;
  readonly totalEquity: number;
}
